<template>
  <div class="vip-page">
    <van-nav-bar
      class="vip-nav"
      :title="$t('VIP Privileges')"
      left-arrow
      @click-left="$router.back()"
    />
    <div class="vip-nav-spacer"></div>

    <!-- Hero：用户 + 当前等级 + 冲下级进度（单一进度条） -->
    <div class="hero">
      <div class="hero-glow"></div>
      <div class="hero-top">
        <img
          class="avatar"
          :src="($store.state.userInfo && $store.state.userInfo.avatar) || '/static/image/imageAvatar02@3x.png'"
          alt="avatar"
        />
        <div class="hero-user">
          <div class="name">{{ displayName }}</div>
          <div class="paysum-line">
            {{ $t('Cumulative deposit') }}
            <em>{{ formatMoney(paysum) }}</em>
          </div>
        </div>
        <div class="badge-current">
          <span class="badge-label">{{ $t('Current Level VIP') }}</span>
          <span class="badge-vip">{{ vipname || ('VIP' + currentVipId) }}</span>
        </div>
      </div>

      <div class="hero-progress">
        <div class="hero-progress-head">
          <span>{{ progressLabel }}</span>
          <span class="hero-progress-nums">
            {{ formatMoney(stagePaysum) }} / {{ formatMoney(progressTarget) }}
          </span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- 等级切换 -->
    <div class="level-tabs">
      <div
        v-for="lv in levels"
        :key="lv.id"
        class="level-tab"
        :class="{
          active: vipTab === lv.id,
          mine: lv.id === currentVipId,
        }"
        @click="vipTab = lv.id"
      >
        <i v-if="lv.id === currentVipId" class="dot"></i>
        {{ lv.vipname || ('VIP' + lv.id) }}
      </div>
    </div>

    <!-- 选中等级概览 -->
    <div class="level-panel">
      <div class="level-panel-head">
        <div class="level-name">{{ selectedLevelName }}</div>
        <div class="level-req">
          {{ $t('Need deposit') }}
          <b>{{ formatMoney(selectedLevelRecharge) }}</b>
        </div>
      </div>
      <div class="level-panel-bar">
        <div class="progress-bar soft">
          <div
            class="progress-fill"
            :style="{ width: selectedLevelProgress + '%' }"
          ></div>
        </div>
        <div class="level-panel-nums">
          {{ formatMoney(selectedLevelPaid) }} / {{ formatMoney(selectedLevelRecharge || 0) }}
          <span v-if="vipTab === currentVipId" class="tag-mine">{{ $t('Current') }}</span>
          <span v-else-if="vipTab < currentVipId" class="tag-ok">{{ $t('Reached') }}</span>
          <span v-else class="tag-lock">{{ $t('Not reached') }}</span>
        </div>
      </div>
    </div>

    <!-- 可领礼金 -->
    <div class="claim-section">
      <div class="section-title">
        <span>{{ selectedLevelName }} {{ $t('Exclusive Privileges') }}</span>
      </div>
      <div class="claim-grid">
        <div
          class="claim-card"
          :class="{ ready: canClaimUpgrade }"
        >
          <div class="claim-icon-wrap">
            <img class="claim-icon" src="/static/image/giftnew.png" alt="" />
          </div>
          <div class="claim-amt">{{ formatMoney(selectedUpgradeAmount) }}</div>
          <div class="claim-label">{{ $t('Promotion Bonus') }}</div>
          <button
            class="claim-btn"
            :disabled="!canClaimUpgrade"
            @click="claimBonus('upgrade')"
          >
            {{ upgradeBtnText }}
          </button>
        </div>
        <div
          class="claim-card"
          :class="{ ready: claims.daily.can_claim && vipTab === currentVipId }"
        >
          <div class="claim-icon-wrap">
            <img class="claim-icon" src="/static/image/jinbi.png" alt="" />
          </div>
          <div class="claim-amt">{{ formatMoney(claims.daily.amount) }}</div>
          <div class="claim-label">{{ $t('Daily Bonus') }}</div>
          <button
            class="claim-btn"
            :disabled="!claims.daily.can_claim || vipTab !== currentVipId"
            @click="claimBonus('daily')"
          >
            {{ dailyBtnText }}
          </button>
        </div>
        <div
          class="claim-card"
          :class="{ ready: claims.weekly.can_claim && vipTab === currentVipId }"
        >
          <div class="claim-icon-wrap">
            <img class="claim-icon" src="/static/image/jinbi2.png" alt="" />
          </div>
          <div class="claim-amt">{{ formatMoney(claims.weekly.amount) }}</div>
          <div class="claim-label">{{ $t('Weekly Bonus') }}</div>
          <button
            class="claim-btn"
            :disabled="!claims.weekly.can_claim || vipTab !== currentVipId"
            @click="claimBonus('weekly')"
          >
            {{ weeklyBtnText }}
          </button>
        </div>
        <div
          class="claim-card"
          :class="{ ready: claims.monthly.can_claim && vipTab === currentVipId }"
        >
          <div class="claim-icon-wrap">
            <img class="claim-icon" src="/static/image/hongbao.png" alt="" />
          </div>
          <div class="claim-amt">{{ formatMoney(claims.monthly.amount) }}</div>
          <div class="claim-label">{{ $t('Monthly Bonus') }}</div>
          <button
            class="claim-btn"
            :disabled="!claims.monthly.can_claim || vipTab !== currentVipId"
            @click="claimBonus('monthly')"
          >
            {{ monthlyBtnText }}
          </button>
        </div>
      </div>
      <div class="claim-tip">{{ $t('VIP claim tip') }}</div>
    </div>

    <!-- 等级奖励表 -->
    <div class="table-section">
      <div class="section-title">{{ $t('VIP Level') }}</div>
      <div class="table-wrap">
        <table class="vip-table">
          <thead>
            <tr>
              <th>{{ $t('VIP Level') }}</th>
              <th>{{ $t('Deposit') }}</th>
              <th>{{ $t('Promotion Bonus') }}</th>
              <th>{{ $t('Daily Bonus') }}</th>
              <th>{{ $t('Weekly Bonus') }}</th>
              <th>{{ $t('Monthly Bonus') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="lv in levels"
              :key="'row-' + lv.id"
              :class="{
                current: isCurrentLevel(lv.id),
                next: isNextLevel(lv.id),
              }"
            >
              <td class="level-cell">
                <div class="level-name-row">
                  <span class="gold">{{ lv.vipname || ('VIP' + lv.id) }}</span>
                  <span v-if="isCurrentLevel(lv.id)" class="chip">{{ $t('Current level tag') }}</span>
                </div>
              </td>
              <td class="recharge-cell">
                <div class="recharge-num">{{ formatMoney(lv.recharge) }}</div>
                <!-- 下一档保留累计充值进度（图一） -->
                <div
                  v-if="isNextLevel(lv.id) || (isCurrentLevel(lv.id) && !nextVipId)"
                  class="row-progress"
                >
                  <div class="progress-bar mini">
                    <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                  </div>
                  <span>{{ formatMoney(stagePaysum) }}/{{ formatMoney(lv.recharge || progressTarget) }}</span>
                </div>
              </td>
              <td>{{ formatMoney(lv.upgrade_bonus) }}</td>
              <td>{{ formatMoney(lv.daily_salary) }}</td>
              <td>{{ formatMoney(lv.weekly_salary) }}</td>
              <td>{{ formatMoney(lv.monthly_salary) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 规则 -->
    <div class="rules">
      <div class="rules-title">{{ $t('VIP Terms and Rules') }}</div>
      <p>
        <strong>{{ $t('Promotion Criteria') }}</strong>
        {{ $t('VIP rule upgrade by deposit') }}
      </p>
      <p>
        <strong>{{ $t('Promotion Bonus') }}</strong>
        {{ $t('VIP rule upgrade bonus') }}
      </p>
      <p>
        <strong>{{ $t('Daily Bonus') }}</strong>
        {{ $t('VIP rule daily') }}
      </p>
      <p>
        <strong>{{ $t('Weekly Bonus') }}</strong>
        {{ $t('VIP rule weekly') }}
      </p>
      <p>
        <strong>{{ $t('Monthly Bonus') }}</strong>
        {{ $t('VIP rule monthly') }}
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'vip',
  data() {
    return {
      levels: [],
      vipTab: 1,
      currentVipId: 1,
      vipname: 'VIP1',
      paysum: 0,
      stagePaysum: 0,
      currentRecharge: 0,
      nextRecharge: 0,
      nextVipId: null,
      nextVipName: '',
      progressPercent: 0,
      claims: {
        upgrade: { amount: 0, claimed: false, can_claim: false },
        daily: { amount: 0, claimed: false, can_claim: false },
        weekly: { amount: 0, claimed: false, can_claim: false },
        monthly: { amount: 0, claimed: false, can_claim: false },
      },
      upgradeByLevel: {},
      claiming: false,
    };
  },
  computed: {
    displayName() {
      const u = this.$store.state.userInfo || {};
      return u.username || u.account || u.name || u.id || '会员';
    },
    selectedLevel() {
      return this.levels.find((l) => l.id === this.vipTab) || this.levels[0] || {};
    },
    selectedLevelName() {
      return this.selectedLevel.vipname || ('VIP' + (this.vipTab || 1));
    },
    selectedLevelRecharge() {
      return Number(this.selectedLevel.recharge || 0);
    },
    selectedLevelPaid() {
      const lv = this.selectedLevel;
      const need = Number(lv.recharge || 0);
      if (need <= 0) return 0;
      const total = Number(lv.required_total);
      const before = Number.isFinite(total) && total > 0 ? total - need : this.requiredBefore(lv.id);
      return Math.max(0, Math.min(this.paysum - before, need));
    },
    selectedLevelProgress() {
      const target = this.selectedLevelRecharge || 1;
      return Math.min(100, Math.round((this.selectedLevelPaid / target) * 10000) / 100);
    },
    progressTarget() {
      return this.nextRecharge > 0 ? this.nextRecharge : (this.currentRecharge || 1);
    },
    progressLabel() {
      if (this.nextVipId) {
        return this.$t('Toward next VIP') + ' ' + (this.nextVipName || ('VIP' + this.nextVipId));
      }
      return this.$t('Max VIP reached');
    },
    selectedUpgradeAmount() {
      return Number(this.selectedLevel.upgrade_bonus || 0);
    },
    canClaimUpgrade() {
      const st = this.upgradeByLevel[this.vipTab];
      return !!(st && st.can_claim);
    },
    upgradeBtnText() {
      const st = this.upgradeByLevel[this.vipTab];
      if (!st || Number(st.amount || 0) <= 0) return this.$t('No bonus');
      if (st.claimed) return this.$t('Claimed');
      if (st.can_claim) return this.$t('Claim');
      return this.$t('Not reached');
    },
    dailyBtnText() {
      return this.periodBtnText(this.claims.daily);
    },
    weeklyBtnText() {
      return this.periodBtnText(this.claims.weekly);
    },
    monthlyBtnText() {
      return this.periodBtnText(this.claims.monthly);
    },
  },
  created() {
    const vip = parseInt((this.$store.state.userInfo && this.$store.state.userInfo.vip) || 1, 10);
    this.vipTab = vip || 1;
    this.currentVipId = vip || 1;
    this.loadVipStatus();
  },
  methods: {
    requiredBefore(levelId) {
      let sum = 0;
      const list = (this.levels || []).slice().sort((a, b) => a.id - b.id);
      for (let i = 0; i < list.length; i++) {
        if (list[i].id >= levelId) break;
        sum += Number(list[i].recharge || 0);
      }
      return sum;
    },
    formatMoney(v) {
      const n = Number(v || 0);
      return n.toFixed(2);
    },
    isCurrentLevel(id) {
      return Number(id) === Number(this.currentVipId) && Number(this.currentVipId) > 0;
    },
    isNextLevel(id) {
      return Number(id) === Number(this.nextVipId);
    },
    periodBtnText(claim) {
      if (!claim || Number(claim.amount || 0) <= 0) return this.$t('No bonus');
      if (this.vipTab !== this.currentVipId) return this.$t('Current level only');
      if (claim.claimed) return this.$t('Claimed');
      if (claim.can_claim) return this.$t('Claim');
      return this.$t('Not reached');
    },
    loadVipStatus() {
      const that = this;
      const app = that.$parent;
      if (app && app.showLoading) app.showLoading();
      that.$apiFun
        .post('/api/vip/status', {})
        .then((res) => {
          if (res.code == 200 && res.data) {
            that.applyStatus(res.data);
          } else {
            that.fallbackLoad();
          }
        })
        .catch(() => that.fallbackLoad())
        .finally(() => {
          if (app && app.hideLoading) app.hideLoading();
        });
    },
    applyStatus(data) {
      this.levels = (data.levels || []).map((lv) => ({
        ...lv,
        id: Number(lv.id),
        recharge: Number(lv.recharge || 0),
        required_total: Number(lv.required_total || 0),
        upgrade_bonus: Number(lv.upgrade_bonus || 0),
        daily_salary: Number(lv.daily_salary || 0),
        weekly_salary: Number(lv.weekly_salary || 0),
        monthly_salary: Number(lv.monthly_salary || 0),
      }));
      this.currentVipId = Number(data.vip || 0);
      this.vipname = data.vipname || (this.currentVipId > 0 ? ('VIP' + this.currentVipId) : 'VIP0');
      this.paysum = Number(data.paysum || 0);
      this.stagePaysum = Number(data.stage_paysum != null ? data.stage_paysum : data.paysum || 0);
      this.currentRecharge = Number(data.current_recharge || 0);
      this.nextRecharge = Number(data.next_recharge || 0);
      this.nextVipName = data.next_vipname || '';
      this.progressPercent = Number(data.progress || 0);
      this.claims = Object.assign(
        {
          upgrade: { amount: 0, claimed: false, can_claim: false },
          daily: { amount: 0, claimed: false, can_claim: false },
          weekly: { amount: 0, claimed: false, can_claim: false },
          monthly: { amount: 0, claimed: false, can_claim: false },
        },
        data.claims || {}
      );
      this.upgradeByLevel = data.upgrade_by_level || {};
      const next = this.levels.find((l) => l.id > this.currentVipId);
      this.nextVipId = next ? next.id : null;
      if (!this.nextVipName && next) this.nextVipName = next.vipname || '';
      if (!this.levels.find((l) => l.id === this.vipTab)) {
        this.vipTab = this.currentVipId > 0
          ? this.currentVipId
          : (this.levels[0] ? this.levels[0].id : 1);
      }
      try {
        const info = JSON.parse(localStorage.getItem('userInfo') || '{}') || {};
        info.vip = this.currentVipId;
        info.paysum = this.paysum;
        localStorage.setItem('userInfo', JSON.stringify(info));
        this.$store.commit('changUserInfo');
      } catch (e) { /* ignore */ }
    },
    fallbackLoad() {
      const that = this;
      that.$apiFun.post('/api/uservip', {}).then((res) => {
        if (res.code == 200 && Array.isArray(res.data)) {
          that.levels = res.data.map((lv) => ({
            id: lv.id,
            vipname: lv.vipname,
            recharge: Number(lv.recharge || 0),
            upgrade_bonus: Number(lv.upgrade_bonus || 0),
            daily_salary: Number(lv.daily_salary || 0),
            weekly_salary: Number(lv.weekly_salary || 0),
            monthly_salary: Number(lv.monthly_salary || 0),
          }));
        }
      });
      that.$apiFun.post('/api/vip/privileges', {}).then((res) => {
        if (res.code == 200 && res.data) {
          Object.keys(res.data).forEach((id) => {
            const row = res.data[id];
            const lv = that.levels.find((x) => String(x.id) === String(id));
            if (lv) {
              lv.upgrade_bonus = Number(row.upgrade_bonus || 0);
              lv.daily_salary = Number(row.daily_salary || 0);
              lv.weekly_salary = Number(row.weekly_salary || 0);
              lv.monthly_salary = Number(row.monthly_salary || 0);
              if (row.recharge != null) lv.recharge = Number(row.recharge);
            }
          });
        }
      });
      const u = that.$store.state.userInfo || {};
      that.paysum = Number(u.paysum || 0);
      that.currentVipId = Number(u.vip || 1);
      that.vipTab = that.currentVipId || (that.levels[0] && that.levels[0].id) || 1;
      const cur = that.levels.find((l) => l.id === that.currentVipId);
      const next = that.levels.find((l) => l.id > that.currentVipId);
      that.currentRecharge = cur ? Number(cur.recharge || 0) : 0;
      that.nextRecharge = next ? Number(next.recharge || 0) : that.currentRecharge;
      that.nextVipId = next ? next.id : null;
      that.nextVipName = next ? next.vipname : '';
      const before = that.requiredBefore(next ? next.id : that.currentVipId);
      that.stagePaysum = Math.max(0, that.paysum - before);
      const target = that.nextRecharge || that.currentRecharge || 1;
      that.progressPercent = Math.min(100, Math.round((that.stagePaysum / target) * 10000) / 100);
    },
    claimBonus(type) {
      if (this.claiming) return;
      const map = {
        upgrade: '/api/claim-upgrade-bonus',
        daily: '/api/claim-daily-salary',
        weekly: '/api/claim-weekly-salary',
        monthly: '/api/claim-monthly-salary',
      };
      const url = map[type];
      if (!url) return;
      const payload = type === 'upgrade' ? { vip_id: this.vipTab } : {};
      const app = this.$parent;
      this.claiming = true;
      if (app && app.showLoading) app.showLoading();
      this.$apiFun
        .post(url, payload)
        .then((res) => {
          if (res.code == 200) {
            if (app && app.showTost) app.showTost(1, res.message || this.$t('Claim success'));
            else alert(res.message || this.$t('Claim success'));
            if (res.data && res.data.balance != null) {
              try {
                const info = JSON.parse(localStorage.getItem('userInfo') || '{}') || {};
                info.balance = res.data.balance;
                localStorage.setItem('userInfo', JSON.stringify(info));
                this.$store.commit('changUserInfo');
              } catch (e) { /* ignore */ }
            }
            this.loadVipStatus();
          } else {
            if (app && app.showTost) app.showTost(0, res.message || this.$t('Claim failed'));
            else alert(res.message || this.$t('Claim failed'));
          }
        })
        .catch(() => {
          if (app && app.showTost) app.showTost(0, this.$t('Claim failed'));
          else alert(this.$t('Claim failed'));
        })
        .finally(() => {
          this.claiming = false;
          if (app && app.hideLoading) app.hideLoading();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.vip-page {
  --vip-bg: #0e1420;
  --vip-card: #161e2e;
  --vip-line: rgba(212, 168, 110, 0.28);
  --vip-gold: #e6b878;
  --vip-gold-2: #c4893f;
  --vip-text: #e9edf5;
  --vip-muted: #8f9bb0;
  min-height: 100vh;
  background:
    radial-gradient(120% 80% at 50% -10%, rgba(196, 137, 63, 0.18), transparent 55%),
    linear-gradient(180deg, #141b2b 0%, var(--vip-bg) 40%, #0b101a 100%);
  color: var(--vip-text);
  padding-bottom: 48px;
}

.vip-nav {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(14, 20, 32, 0.92) !important;
  backdrop-filter: blur(8px);
  z-index: 20;
}
.vip-nav ::v-deep .van-nav-bar__title,
.vip-nav ::v-deep .van-icon {
  color: #fff !important;
}
.vip-nav ::v-deep .van-hairline--bottom::after {
  border-color: rgba(255, 255, 255, 0.06);
}
.vip-nav-spacer {
  height: 46px;
}

.hero {
  position: relative;
  margin: 12px 14px 10px;
  padding: 16px;
  border-radius: 18px;
  overflow: hidden;
  background:
    linear-gradient(145deg, rgba(232, 196, 140, 0.16), transparent 42%),
    linear-gradient(180deg, #1c2638 0%, #141c2c 100%);
  border: 1px solid var(--vip-line);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);
}
.hero-glow {
  position: absolute;
  right: -30px;
  top: -40px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(230, 184, 120, 0.35), transparent 70%);
  pointer-events: none;
}
.hero-top {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
}
.avatar {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(230, 184, 120, 0.85);
  box-shadow: 0 0 0 3px rgba(230, 184, 120, 0.12);
}
.hero-user {
  flex: 1;
  min-width: 0;
}
.name {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.2px;
}
.paysum-line {
  margin-top: 4px;
  font-size: 12px;
  color: var(--vip-muted);
  em {
    font-style: normal;
    color: var(--vip-gold);
    font-weight: 700;
    margin-left: 4px;
  }
}
.badge-current {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.badge-label {
  font-size: 10px;
  color: var(--vip-muted);
}
.badge-vip {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  color: #2a1a0a;
  background: linear-gradient(135deg, #f3d7a6, #d4a05a);
  box-shadow: 0 4px 12px rgba(196, 137, 63, 0.35);
}

.hero-progress {
  position: relative;
  margin-top: 16px;
}
.hero-progress-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #d7c3a4;
}
.hero-progress-nums {
  color: var(--vip-gold);
  font-weight: 600;
  white-space: nowrap;
}

.progress-bar {
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
  &.soft {
    height: 6px;
    background: rgba(139, 90, 43, 0.12);
  }
  &.mini {
    height: 4px;
    margin: 5px 0 3px;
    background: rgba(255, 255, 255, 0.1);
  }
}
.progress-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #a86d2f, #e0b06a 55%, #fff0d4);
  box-shadow: 0 0 10px rgba(230, 184, 120, 0.35);
  transition: width 0.35s ease;
}

.level-tabs {
  display: flex;
  overflow-x: auto;
  gap: 8px;
  padding: 4px 14px 12px;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
}
.level-tab {
  position: relative;
  flex: 0 0 auto;
  min-width: 58px;
  padding: 9px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: var(--vip-muted);
  font-size: 12px;
  text-align: center;
  transition: all 0.2s ease;
  &.mine:not(.active) {
    border-color: rgba(230, 184, 120, 0.35);
    color: #d7c3a4;
  }
  &.active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #c4893f, #8f501f);
    box-shadow: 0 6px 14px rgba(143, 80, 31, 0.35);
  }
  .dot {
    display: inline-block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #e6b878;
    margin-right: 3px;
    vertical-align: middle;
  }
  &.active .dot {
    background: #fff;
  }
}

.level-panel {
  margin: 0 14px 14px;
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(145deg, #f4e6cf 0%, #e4c9a0 48%, #d7b07a 100%);
  color: #6b4320;
  box-shadow: 0 10px 22px rgba(120, 78, 30, 0.22);
}
.level-panel-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}
.level-name {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #5a3514;
}
.level-req {
  font-size: 12px;
  b {
    font-size: 14px;
    margin-left: 4px;
  }
}
.level-panel-bar {
  margin-top: 12px;
}
.level-panel-nums {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 11px;
  color: #7a5230;
}
.tag-mine,
.tag-ok,
.tag-lock {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
}
.tag-mine {
  background: rgba(90, 53, 20, 0.14);
  color: #5a3514;
}
.tag-ok {
  background: rgba(46, 125, 50, 0.15);
  color: #2e7d32;
}
.tag-lock {
  background: rgba(0, 0, 0, 0.08);
  color: #7a5230;
}

.claim-section,
.table-section,
.rules {
  margin: 0 14px 18px;
}
.section-title,
.rules-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--vip-gold);
  margin-bottom: 12px;
  &::before {
    content: '';
    width: 3px;
    height: 14px;
    border-radius: 2px;
    background: linear-gradient(180deg, #f0d2a0, #c4893f);
  }
}

.claim-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.claim-card {
  position: relative;
  background: linear-gradient(180deg, #1b2436 0%, #151d2c 100%);
  border: 1px solid rgba(230, 184, 120, 0.16);
  border-radius: 14px;
  padding: 14px 12px 12px;
  text-align: center;
  overflow: hidden;
  &.ready {
    border-color: rgba(230, 184, 120, 0.55);
    box-shadow: 0 0 0 1px rgba(230, 184, 120, 0.08), 0 8px 18px rgba(0, 0, 0, 0.22);
  }
}
.claim-icon-wrap {
  width: 42px;
  height: 42px;
  margin: 0 auto 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(230, 184, 120, 0.18);
}
.claim-icon {
  width: 30px;
  height: 30px;
  object-fit: contain;
  display: block;
}
.claim-amt {
  font-size: 20px;
  font-weight: 800;
  color: var(--vip-gold);
  letter-spacing: 0.3px;
}
.claim-label {
  margin: 4px 0 12px;
  font-size: 12px;
  color: #c5cede;
}
.claim-btn {
  width: 100%;
  border: 0;
  border-radius: 10px;
  padding: 9px 0;
  background: linear-gradient(135deg, #d4a05a, #9a5a22);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 6px 14px rgba(154, 90, 34, 0.28);
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  &:disabled {
    opacity: 1;
    color: #9aa3b5;
    background: #2a3345;
    box-shadow: none;
  }
}
.claim-tip {
  margin-top: 10px;
  font-size: 11px;
  line-height: 1.5;
  color: var(--vip-muted);
}

.table-wrap {
  overflow-x: auto;
  border-radius: 14px;
  border: 1px solid rgba(230, 184, 120, 0.16);
  background: var(--vip-card);
  -webkit-overflow-scrolling: touch;
}
.vip-table {
  width: 100%;
  min-width: 540px;
  border-collapse: collapse;
  font-size: 12px;
  th {
    position: sticky;
    top: 0;
    background: #223049;
    color: var(--vip-gold);
    padding: 12px 8px;
    white-space: nowrap;
    font-weight: 700;
  }
  td {
    padding: 12px 8px;
    text-align: center;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    color: #d5dceb;
  }
  tr.current td {
    background: rgba(196, 137, 63, 0.22);
  }
  tr.current td:first-child {
    box-shadow: inset 3px 0 0 #e6b878;
  }
  tr.next td {
    background: rgba(230, 184, 120, 0.06);
  }
  .level-cell {
    min-width: 92px;
  }
  .level-name-row {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
  }
  .gold {
    color: var(--vip-gold);
    font-weight: 700;
    white-space: nowrap;
  }
  .chip {
    display: inline-block;
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 800;
    line-height: 1.3;
    color: #2a1a0a;
    background: linear-gradient(135deg, #f3d7a6, #d4a05a);
    white-space: nowrap;
    box-shadow: 0 2px 6px rgba(196, 137, 63, 0.35);
  }
  .recharge-cell {
    min-width: 88px;
  }
  .recharge-num {
    font-weight: 600;
  }
  .row-progress {
    margin-top: 2px;
    font-size: 10px;
    color: #9aa3b5;
    line-height: 1.3;
  }
}

.rules {
  background: rgba(22, 30, 46, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  padding: 14px 14px 6px;
  p {
    margin: 0 0 12px;
    font-size: 12px;
    line-height: 1.65;
    color: #a8b2c5;
    strong {
      display: block;
      margin-bottom: 2px;
      color: var(--vip-gold);
      font-weight: 700;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/vip.vue